// Mobile (Play Store) — setup guide + verify-play-purchase diagnostics.
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Smartphone, Key, BarChart3, CheckCircle2, Stethoscope, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface DiagResult {
  ok: boolean;
  /** Plain-English verdict shown to the admin. */
  verdict: string;
  /** Raw JSON / error returned from the edge function. */
  raw: string;
}

/** Classify the verify-play-purchase response into a verdict.
 *  A clean "Play API 400" with "invalid" in the body means wiring is fine —
 *  Google rejected our fake token as expected. Anything else points to a
 *  specific misconfiguration. */
function classify(payload: unknown, status: number, fnError?: string): DiagResult {
  const raw = JSON.stringify(payload ?? { error: fnError }, null, 2);
  const errStr = (
    (payload as { error?: string } | null)?.error ?? fnError ?? ""
  ).toLowerCase();
  const prefix = status ? `HTTP ${status} — ` : "";

  if (status >= 200 && status < 300 && !errStr) {
    return { ok: false, verdict: "Unexpected success — Google should have rejected a fake token.", raw };
  }
  if (errStr.includes("not configured") || errStr.includes("not valid json")) {
    return { ok: false, verdict: `❌ ${prefix}GOOGLE_PLAY_SERVICE_ACCOUNT_JSON secret is missing or malformed.`, raw };
  }
  if (errStr.includes("oauth token exchange")) {
    return { ok: false, verdict: `❌ ${prefix}Service account credentials rejected by Google OAuth — JSON is wrong or service account disabled.`, raw };
  }
  if (errStr.includes("insufficient permissions") || errStr.includes("play api 401") || (status === 401 && !errStr.includes("unauthorized"))) {
    return {
      ok: false,
      verdict: `❌ ${prefix}Play Console service-account permission problem. Open Play Console → Setup → API access → find fitaura-play-verifier@…iam.gserviceaccount.com → Manage permissions → add app access for com.fitauraai.app with 'View financial data, orders…' + 'Manage orders and subscriptions' + 'View app information…'. Save and wait 5–15 min.`,
      raw,
    };
  }
  if (status === 401 || errStr === "unauthorized") {
    return { ok: false, verdict: `❌ ${prefix}You're not logged in as an admin in this preview — sign in first, then retry.`, raw };
  }
  if (errStr.includes("play api 403")) {
    return { ok: false, verdict: `❌ ${prefix}API/app access not linked. In Play Console → Setup → API access, link the Cloud project and grant this service account access to com.fitauraai.app.`, raw };
  }
  if (errStr.includes("play api 404") || errStr.includes("productnotfound") || errStr.includes("not found")) {
    return { ok: true, verdict: `✅ ${prefix}Play returned 'token/product not found' for a fake token — that's expected. Wiring + permissions are correct; real purchases will verify. (If a real purchase 404s, the SKU isn't Active in Play Console.)`, raw };
  }
  if (errStr.includes("play api 400") || errStr.includes("invalid")) {
    return { ok: true, verdict: `✅ ${prefix}Wiring is correct. Google rejected the fake token as expected — real purchases will verify.`, raw };
  }
  return { ok: false, verdict: `⚠️ ${prefix}Unrecognised error — read the raw response below.`, raw };
}

export default function AdminMobile() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<DiagResult | null>(null);

  async function runDiagnostic() {
    setRunning(true);
    setResult(null);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) {
        setResult({ ok: false, verdict: "❌ Not signed in — log in as an admin first.", raw: "no session" });
        return;
      }
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-play-purchase`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          purchaseToken: `diagnostic_fake_token_${Date.now()}`,
          productId: "credits_mini",
          packId: "mini",
        }),
      });
      const text = await res.text();
      let parsed: unknown = null;
      try { parsed = JSON.parse(text); } catch { parsed = { error: text }; }
      setResult(classify(parsed, res.status));
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setResult({ ok: false, verdict: `❌ Edge function call failed: ${msg}`, raw: msg });
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">Mobile · Play Store</h1>
        <p className="text-sm text-muted-foreground">
          Live installs, crashes, ANRs and ratings for{" "}
          <code className="rounded bg-muted px-1 text-[11px]">com.fitauraai.app</code>.
        </p>
      </div>

      {/* Diagnostics card — top so it's the first thing visible. */}
      <Card>
        <CardHeader className="flex flex-row items-start gap-3">
          <Stethoscope className="mt-1 h-5 w-5 text-primary" />
          <div className="flex-1">
            <CardTitle className="text-base">Play Billing diagnostics</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              Calls <code className="rounded bg-muted px-1">verify-play-purchase</code> with a fake
              token. A clean "Play API 400: invalid token" response means the service account JSON
              is loaded and Google Play API is reachable — i.e. real purchases will work.
            </p>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button onClick={runDiagnostic} disabled={running} size="sm">
            {running ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Calling verifier…
              </>
            ) : (
              "Test Play Verifier"
            )}
          </Button>

          {result && (
            <div className="space-y-2">
              <div
                className={`rounded-md border p-3 text-sm font-medium ${
                  result.ok
                    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                    : "border-destructive/40 bg-destructive/10 text-destructive"
                }`}
              >
                {result.verdict}
              </div>
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground">Raw response</summary>
                <pre className="mt-2 overflow-x-auto rounded bg-muted p-2 text-[11px]">
                  {result.raw}
                </pre>
              </details>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-dashed">
        <CardHeader className="flex flex-row items-start gap-3">
          <Smartphone className="mt-1 h-5 w-5 text-primary" />
          <div>
            <CardTitle className="text-base">Connect the Play Console</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              Live Play data needs a Google Cloud service account with read access to your Play
              Console. Once configured, this page will show daily installs, uninstalls,
              crash-free %, ANR rate, ratings and the latest reviews.
            </p>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Step
            n={1}
            icon={<Key className="h-4 w-4" />}
            title="Create a Google Cloud service account"
            body="In Google Cloud Console → IAM → Service Accounts → Create. Generate a JSON key and download it."
          />
          <Step
            n={2}
            icon={<CheckCircle2 className="h-4 w-4" />}
            title="Grant Play Console access"
            body="Play Console → Setup → API access → Link the service account. Give it View app information, View financial data, and View Android vitals permissions."
          />
          <Step
            n={3}
            icon={<BarChart3 className="h-4 w-4" />}
            title="Add the secret to Lovable Cloud"
            body="Paste the entire JSON key into a new secret named GOOGLE_PLAY_SA_JSON. After that, ask me to wire up the admin-play-console edge function and this page will start showing real data."
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 text-xs text-muted-foreground">
          Until then, you can still see Play <strong>revenue</strong> on the Revenue page (synced
          from your Play purchase webhooks) and Play <strong>refunds</strong> on the Refunds page.
        </CardContent>
      </Card>
    </div>
  );
}

function Step({ n, icon, title, body }: { n: number; icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="flex gap-3 rounded-lg border p-3">
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
        <span className="text-xs font-semibold">{n}</span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-sm font-medium">
          {icon}
          {title}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{body}</p>
      </div>
    </div>
  );
}
